import { ApiUrlInput } from '@neo/blocks/api-url-input'
import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { CustomCompType, GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@neo/primitives/tabs'
import { Tooltip } from '@neo/primitives/tooltip'
import { useSheet } from '@shared/hooks/use-sheet'
import { Lock } from 'lucide-react'
import { useState } from 'react'
import { SubAccountsSelect } from 'v2/api-console/shared/components/subaccount-form-select'
import { useUserPlan } from 'v2/shared/hooks/use-plan'
import {
  LOCKED_TOOLTIP,
  openBuddyEnterpriseRequest,
} from 'v2/shared/utils/buddy-enterprise-request'
import type {
  Credential,
  IPAccessControlList,
  ROriginationURIInternal,
} from 'v2/types/api/settings'
import { z } from 'zod'
import type { PlatformGateOptions } from './platform-field.config'
import { getTrunkFields } from './trunk-fields.config'
import { ApplicationType, type UnifiedApplicationFormValues } from './types'

const voiceFields: GenericFieldConfig<UnifiedApplicationFormValues> = [
  {
    name: 'answer_url',
    type: 'custom',
    label: 'Answer URL',
    className: 'col-span-2',
    component: ({ form }) => (
      <ApiUrlInput
        methods={['GET', 'POST']}
        defaultMethod={form.state.values.answer_method || 'POST'}
        url={form.state.values.answer_url || ''}
        placeholder="https://example.com/answer"
        onMethodChange={(method) => {
          form.setFieldValue('answer_method', method as 'GET' | 'POST')
        }}
        onUrlChange={(url) => {
          form.setFieldValue('answer_url', url)
          form.validateField('answer_url', 'change')
        }}
        id="answer-url-input"
        label="Answer URL"
      />
    ),
    zodValidation: (values) =>
      values.application_type === ApplicationType.XML
        ? z
            .union([
              z.string().url('Please enter a valid URL'),
              z.literal(''),
              z.null(),
              z.undefined(),
            ])
            .optional()
        : z.any(),
  },
  {
    name: 'hangup_url',
    type: 'custom',
    label: 'Hangup URL',
    className: 'col-span-2',
    component: ({ form }) => (
      <ApiUrlInput
        methods={['GET', 'POST']}
        defaultMethod={form.state.values.hangup_method || 'POST'}
        url={form.state.values.hangup_url || ''}
        placeholder="https://example.com/hangup"
        onMethodChange={(method) => {
          form.setFieldValue('hangup_method', method as 'GET' | 'POST')
        }}
        onUrlChange={(url) => {
          form.setFieldValue('hangup_url', url)
          form.validateField('hangup_url', 'change')
        }}
        id="hangup-url-input"
        label="Hangup URL"
      />
    ),
    zodValidation: (values) =>
      values.application_type === ApplicationType.XML
        ? z
            .union([
              z.string().url('Please enter a valid URL'),
              z.literal(''),
              z.null(),
              z.undefined(),
            ])
            .optional()
        : z.any(),
  },
  {
    name: 'fallback_answer_url',
    type: 'custom',
    label: 'Fallback Answer URL',
    className: 'col-span-2',
    component: ({ form }) => (
      <ApiUrlInput
        methods={['GET', 'POST']}
        defaultMethod={form.state.values.fallback_method || 'POST'}
        url={form.state.values.fallback_answer_url || ''}
        placeholder="https://example.com/fallback"
        onMethodChange={(method) => {
          form.setFieldValue('fallback_method', method as 'GET' | 'POST')
        }}
        onUrlChange={(url) => {
          form.setFieldValue('fallback_answer_url', url)
          form.validateField('fallback_answer_url', 'change')
        }}
        id="fallback-answer-url-input"
        label="Fallback Answer URL"
      />
    ),
    zodValidation: (values) =>
      values.application_type === ApplicationType.XML
        ? z
            .union([
              z.string().url('Please enter a valid URL'),
              z.literal(''),
              z.null(),
              z.undefined(),
            ])
            .optional()
        : z.any(),
  },
  {
    name: 'public_uri',
    componentType: 'checkbox',
    getDynamicProps: () => ({
      inputProps: {
        label: 'Public URI',
        className: 'items-start',
      },
    }),
    className: 'col-span-1',
    zodValidation: (values) =>
      values.application_type === ApplicationType.XML ? z.boolean().default(false) : z.any(),
  },
  {
    name: 'default_endpoint_app',
    componentType: 'checkbox',
    getDynamicProps: () => ({
      inputProps: {
        label: 'Default Endpoint Application',
        className: 'items-start',
      },
    }),
    className: 'col-span-1',
    zodValidation: (values) =>
      values.application_type === ApplicationType.XML ? z.boolean().default(false) : z.any(),
  },
]

// Only consumed by the Messaging tab below (a locked plan never renders it).
const messageFields: GenericFieldConfig<UnifiedApplicationFormValues> = [
  {
    name: 'message_url',
    type: 'custom',
    label: 'Message URL',
    className: 'col-span-2',
    component: ({ form }) => (
      <ApiUrlInput
        methods={['GET', 'POST']}
        defaultMethod={form.state.values.message_method || 'POST'}
        url={form.state.values.message_url || ''}
        placeholder="https://example.com/message"
        onMethodChange={(method) => {
          form.setFieldValue('message_method', method as 'GET' | 'POST')
        }}
        onUrlChange={(url) => {
          form.setFieldValue('message_url', url)
          form.validateField('message_url', 'change')
        }}
        id="message-url-input"
        label="Message URL"
      />
    ),
    zodValidation: (values) =>
      values.application_type === ApplicationType.XML
        ? z
            .union([
              z.string().url('Please enter a valid URL'),
              z.literal(''),
              z.null(),
              z.undefined(),
            ])
            .optional()
        : z.any(),
  },
  {
    name: 'log_incoming_messages',
    componentType: 'checkbox',
    getDynamicProps: () => ({
      inputProps: {
        label: 'Redact Incoming Messages',
        className: 'items-start',
      },
    }),
    className: 'col-span-2',
    zodValidation: (values) =>
      values.application_type === ApplicationType.XML ? z.boolean().default(true) : z.any(),
  },
]

const VoiceMessagingSections = ({
  form,
}: {
  form: GenericFormType<UnifiedApplicationFormValues>
}) => {
  const applicationType = form.useStore((state) => state.values.application_type)
  const { closeSheet } = useSheet()
  // Messaging is Enterprise-gated: Enterprise accounts get a fully working
  // Messaging tab (message URL + settings); non-Enterprise plans see it ghosted
  // with a lock, and clicking it opens the Buddy upgrade request instead of
  // switching tabs.
  const { isEnterpriseLocked } = useUserPlan()
  const [activeTab, setActiveTab] = useState('voice')

  if (applicationType !== ApplicationType.XML) return null

  const handleTabChange = (value: string) => {
    if (value === 'messaging' && isEnterpriseLocked) {
      // Close the sheet first so the Buddy assistant panel it opens is visible.
      closeSheet()
      openBuddyEnterpriseRequest({ source: 'messaging' })
      return
    }
    setActiveTab(value)
  }

  return (
    <Tabs
      value={isEnterpriseLocked ? 'voice' : activeTab}
      onValueChange={handleTabChange}
      className="col-span-2"
    >
      <TabsList variant="simple" className="mb-3">
        <TabsTrigger value="voice" variant="simple" className="pl-0" data-testid="tab-app-voice">
          Voice
        </TabsTrigger>
        <TabsTrigger value="messaging" variant="simple" data-testid="tab-app-messaging">
          {isEnterpriseLocked ? (
            <Tooltip content={LOCKED_TOOLTIP} delayDuration={300}>
              <span className="flex items-center gap-1.5 opacity-50">
                Messaging
                <Lock className="h-3 w-3" strokeWidth={2.5} />
              </span>
            </Tooltip>
          ) : (
            'Messaging'
          )}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="voice">
        <div className="grid gap-4 bg-bg3 p-3 rounded-md">
          <FieldsRenderer fields={voiceFields} form={form} />
        </div>
      </TabsContent>
      {!isEnterpriseLocked && (
        <TabsContent value="messaging">
          <div className="grid gap-4 bg-bg3 p-3 rounded-md">
            <FieldsRenderer fields={messageFields} form={form} />
          </div>
        </TabsContent>
      )}
    </Tabs>
  )
}

export const getUnifiedApplicationFormFields = (
  isEditing: boolean = false,
  ipAccessControlLists: IPAccessControlList[] = [],
  credentials: Credential[] = [],
  originationURIs: ROriginationURIInternal[] = [],
  onCreateNewIPACL: () => void = () => {},
  onEditIPACL: (ipacl: IPAccessControlList) => void = () => {},
  onCreateNewCredential: () => void = () => {},
  onEditCredential: (credential: Credential) => void = () => {},
  onCreateNewOriginationURI: () => void = () => {},
  onEditOriginationURI: (uri: ROriginationURIInternal) => void = () => {},
  onTrunkDirectionChange?: (isOutbound: boolean) => void,
  onApplicationTypeChange?: (isSipTrunk: boolean) => void,
  gateOptions: PlatformGateOptions = {},
): GenericFieldConfig<UnifiedApplicationFormValues> => [
  {
    name: 'application_type',
    componentType: 'radioGroup',
    label: 'Select Type',
    inputProps: {
      options: [
        {
          label: (
            <div className="flex flex-col gap-1">
              <div className="flex items-center">
                <span className="text-p-500">Application</span>
              </div>
              <span className="text-s-400 text-muted-foreground">
                When a call comes in, Plivo reads XML instructions from your specified URL to
                execute actions like recording the call, playing a message, or gathering keypad
                input.
              </span>
            </div>
          ),
          value: 'xml',
        },
        {
          label: (
            <div className="flex flex-col gap-1">
              <div className="flex items-center">
                <span className="text-p-500">SIP Trunking</span>
              </div>
              <span className="text-s-400 text-muted-foreground">
                Enables your existing phone system to make and receive calls over the internet.
                Choose this option if you want to connect your PBX server (ex- LiveKit, Asterisk
                etc) to PSTN.
              </span>
            </div>
          ),
          value: 'sip',
        },
      ],
      optionsClassName: 'flex flex-col gap-4',
      optionClassName:
        'rounded-lg border p-4 transition-colors hover:border-primary/50 [&.checked]:border-primary [&.checked]:bg-primary/5',
    },
    className: 'col-span-2',
    eventDataExtractor: (e: string) => e,
    zodValidation: z.nativeEnum(ApplicationType).default(ApplicationType.XML),
    visible: () => false, // Hidden - type is determined by context (SIP Trunking vs Applications page)
    afterOnChange: (values, _, form) => {
      if ('application_type' in values) {
        const isSipTrunk = values.application_type === ApplicationType.SIP
        onApplicationTypeChange?.(isSipTrunk)
        if (isSipTrunk) {
          form?.setFieldValue('trunk_direction', 'outbound')
          onTrunkDirectionChange?.(true)
        }
      }
    },
  },
  {
    name: 'name',
    componentType: 'input',
    getDynamicProps: (values) => ({
      inputProps: {
        label: values.application_type === ApplicationType.SIP ? 'Trunk Name' : 'Application Name',
        placeholder:
          values.application_type === ApplicationType.SIP
            ? 'Enter trunk name'
            : 'Enter application name',
        autoComplete: 'off',
        'data-1p-ignore': 'true',
      },
    }),
    onChangeListenTo: ['application_type'],
    className: 'col-span-2',
    eventDataExtractor: (e, form?: GenericFormType<UnifiedApplicationFormValues>) =>
      form?.state?.values?.application_type === ApplicationType.XML
        ? z
            .string()
            .transform((val) => val.replace(/[^a-zA-Z0-9_-]/g, '_'))
            .parse(e.target?.value)
        : e.target?.value,
    zodValidation: (values) =>
      values.application_type === ApplicationType.XML
        ? z
            .string({ required_error: 'Application name is required' })
            .min(1, 'Application name is required')
            .max(120, 'Name must be less than 130 characters')
        : z
            .string({ required_error: 'Trunk name is required' })
            .min(1, 'Trunk name is required')
            .max(60, 'Name must be less than 60 characters'),
  },

  // XML Application Fields (visible when application_type === 'xml')
  {
    name: 'default_number_app',
    componentType: 'checkbox',
    onChangeListenTo: ['application_type'],
    visible: (values) => values.application_type === ApplicationType.XML,
    getDynamicProps: () => ({
      inputProps: {
        label: 'Default Number Application',
        className: 'items-start',
      },
    }),
    className: 'col-span-2',
    zodValidation: (values) =>
      values.application_type === ApplicationType.XML ? z.boolean().default(false) : z.any(),
  },
  {
    name: 'voice_messaging_sections',
    type: 'basic',
    // Passed by REFERENCE, not wrapped in an inline arrow: an arrow here is a new
    // component identity on every parent render, so React remounts this subtree
    // (failure mode 19) and its useUserPlan/useProfileQuery subscription refetches
    // /profile every time. `type: 'basic'` has no `visible`, so on a SIP sheet this
    // still mounts and renders null (its early return sits below its hooks), but a
    // stable identity means ONE profile subscription instead of one per render.
    component: VoiceMessagingSections,
  },
  {
    name: 'subaccount',
    type: 'custom',
    label: 'Subaccount',
    onChangeListenTo: ['application_type'],
    visible: (values) => values.application_type === ApplicationType.XML,
    component: SubAccountsSelect as CustomCompType<UnifiedApplicationFormValues>,
    className: 'col-span-2',
    // clearable replaces the old-console "----" (Nil sentinel) option with the X
    // clear affordance — same pattern as the Sub Account field on configure-number.
    // Clearing yields '' which resolveSubaccountPayload maps to trace's unlink.
    inputProps: {
      placeholder: 'Select',
      clearable: true,
    },
    eventDataExtractor: (e: string) => e,
    zodValidation: (values) =>
      values.application_type === ApplicationType.XML ? z.string().optional() : z.any(),
  },
  // SIP Trunk Fields (visible when application_type === 'sip')
  ...getTrunkFields(
    ipAccessControlLists,
    credentials,
    originationURIs,
    onCreateNewIPACL,
    onEditIPACL,
    onCreateNewCredential,
    onEditCredential,
    onCreateNewOriginationURI,
    onEditOriginationURI,
    isEditing,
    onTrunkDirectionChange,
    onApplicationTypeChange,
    gateOptions,
  ),
]

// Backward compatibility - default export for non-editing scenarios
export const unifiedApplicationFormFields = () => getUnifiedApplicationFormFields(false)
